import React from "react";
import { Progress } from "antd";
import { CheckCircleFilled, CloseCircleFilled, CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { useTheme } from "../../../contexts/ThemeContext";
import { Button } from "../../ui";

/**
 * Painel flutuante (canto inferior direito) com o progresso das importações de orçamento da Omie.
 * Fica visível em qualquer tela; some para a sync cuja tela de orçamento está aberta (lá o progresso é inline).
 */
const OmieBudgetImportTracker = ({ imports, hiddenSyncIds, onDismiss, onOpen }) => {
  const { theme } = useTheme();
  const visible = Object.values(imports).filter((imp) => !hiddenSyncIds.has(imp.syncId) && !imp.consumed);
  if (visible.length === 0) return null;

  return (
    <div style={{
      position: "fixed",
      right: 16,
      bottom: 16,
      zIndex: 1000,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      width: "min(340px, calc(100vw - 32px))",
    }}>
      {visible.map((imp) => {
        const running = imp.status === "running";
        const failed = imp.status === "failed";
        const icon = running
          ? <LoadingOutlined style={{ color: theme.primary }} />
          : failed
            ? <CloseCircleFilled style={{ color: "#ff4d4f" }} />
            : <CheckCircleFilled style={{ color: "#52c41a" }} />;
        const title = running
          ? `Importando orçamento ${imp.ano} da Omie`
          : failed
            ? `Falha ao importar orçamento ${imp.ano}`
            : `Orçamento ${imp.ano} da Omie pronto`;

        return (
          <div
            key={imp.syncId}
            role="status"
            style={{
              background: theme.cardBackground,
              border: `1px solid ${theme.border}`,
              borderRadius: 8,
              padding: 12,
              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.12)",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <span style={{ marginTop: 2 }}>{icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: theme.textPrimary, fontSize: 14 }}>{title}</div>
                {imp.syncName && (
                  <div style={{ color: theme.textSecondary, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {imp.syncName}
                  </div>
                )}
              </div>
              {!running && (
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  aria-label="Fechar"
                  onClick={() => onDismiss(imp.syncId)}
                />
              )}
            </div>

            {running && (
              <>
                <Progress percent={imp.percentual} size="small" status="active" style={{ margin: "8px 0 0" }} />
                <div style={{ color: theme.textSecondary, fontSize: 12 }}>
                  {`${imp.mesesConcluidos} de ${imp.totalMeses} meses consultados`}
                </div>
              </>
            )}
            {failed && (
              <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 6 }}>{imp.mensagem}</div>
            )}
            {imp.status === "completed" && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <Button type="primary" size="small" onClick={() => onOpen(imp)}>
                  Abrir orçamento
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OmieBudgetImportTracker;
